import { createContext, useContext } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@apollo/client';
import { Center } from '@chakra-ui/react';
import { RootCompanyDocument } from 'Main/SecuredRoutes/graphql/rootCompany.gql';

import { InsurancesPerks, ProviderPlanId } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { FCWithChildren } from 'common/types';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { OfferDetailsFromSource } from 'features/Insurance/types';

import { NewInsurancesCoverageDocument } from './newInsurancesCoverage.gql';
import { InsurancesCoverage, OfferByGroups } from './types';
import { getOffersByGroup } from './utils';

interface RouteState {
  from?: OfferDetailsFromSource;
}

type Context = {
  coveredInsurancesCount: number;
  uncoveredInsurancesCount: number;
  groups: OfferByGroups;
  coverage: InsurancesCoverage;
  perks: InsurancesPerks;
  planId: ProviderPlanId;
  routeState: RouteState;
};

const NewInsuranceCoverageContext = createContext<Context | undefined>(
  undefined,
);

export const NewInsuranceCoverageProvider: FCWithChildren = ({ children }) => {
  const { state: routeState } = useLocation<RouteState>();
  const companyContext = useCompanyContext();
  const { companyProfileId = '' } = companyContext;

  const { data: companyData, loading: loadingCompany } = useQuery(
    RootCompanyDocument,
    {
      fetchPolicy: 'cache-first',
      onError: () => companyContext.clearCompanyContext(),
      skip: companyProfileId === '',
      variables: { companyProfileId: companyProfileId as string },
    },
  );

  const {
    data: newInsurancesCoverageData,
    error,
    loading,
  } = useQuery(NewInsurancesCoverageDocument, {
    skip: !companyProfileId,
    variables: { companyProfileId },
  });

  const planId = companyData?.viewer.company.currentPlan.providerPlanId;

  if (error) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  if (
    !companyProfileId ||
    loading ||
    !newInsurancesCoverageData ||
    !companyData ||
    loadingCompany ||
    !planId
  ) {
    return <Loader />;
  }

  const { insurances } = newInsurancesCoverageData.viewer.company.currentPlan;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { __typename, ...coverage } = insurances.coverageFrom2024;

  const groups = getOffersByGroup(coverage);
  const offers = [...groups.values()].flatMap((group) => group.offers);

  return (
    <NewInsuranceCoverageContext.Provider
      value={{
        coverage,
        coveredInsurancesCount: offers.filter((offer) => offer.isCovered)
          .length,
        groups,
        perks: insurances.perks,
        planId,
        routeState,
        uncoveredInsurancesCount: offers.filter((offer) => !offer.isCovered)
          .length,
      }}
    >
      {children}
    </NewInsuranceCoverageContext.Provider>
  );
};

export const useNewInsuranceCoverage = () => {
  const context = useContext(NewInsuranceCoverageContext);

  if (!context) {
    throw new Error(
      '"useNewInsuranceCoverage" can only be used under NewInsuranceCoverageProvider',
    );
  }

  return context;
};
